/**
 * Browser-local per-row "last seen comments at" timestamp store.
 *
 * Phase-1 read tracking: a single localStorage key holds a JSON map keyed by
 * `${trackerId}:${rowKey}`. The badge's unread state is derived by comparing a
 * row's latest-comment `updatedAt` against this value. Phase-2 would replace
 * this with a per-user server-side table.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'trackers-board.row-comments.last-seen.v1';

type Store = Record<string, string>;

const EMPTY_STORE: Store = Object.freeze({}) as Store;

/** Composite key so comment reads are scoped per row within a tracker. */
export function rowSeenKey(trackerId: string, rowKey: string): string {
  return `${trackerId}:${rowKey}`;
}

// `useSyncExternalStore` requires referential stability: same input → same
// reference. We cache the last raw string + its parsed object so repeat reads
// without a write return the *same* Store instance, avoiding infinite render
// loops in subscribed components.
let cachedRaw: string | null = null;
let cachedStore: Store = EMPTY_STORE;

function readStore(): Store {
  if (typeof window === 'undefined') return EMPTY_STORE;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return EMPTY_STORE;
  }
  if (raw === cachedRaw) return cachedStore;
  cachedRaw = raw;
  if (!raw) {
    cachedStore = EMPTY_STORE;
    return cachedStore;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') {
      cachedStore = parsed as Store;
    } else {
      cachedStore = EMPTY_STORE;
    }
  } catch {
    cachedStore = EMPTY_STORE;
  }
  return cachedStore;
}

function writeStore(next: Store): void {
  if (typeof window === 'undefined') return;
  const raw = JSON.stringify(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, raw);
  } catch {
    /* quota / private-mode — silently drop */
  }
  // Update the cache in lock-step so the next read returns the new object
  // with the *same* reference until the next write.
  cachedRaw = raw;
  cachedStore = next;
  // Same-tab subscribers don't get a "storage" event; dispatch a custom one.
  window.dispatchEvent(new CustomEvent('trackers-board:comments-last-seen-changed'));
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const storage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback();
  };
  const custom = () => callback();
  window.addEventListener('storage', storage);
  window.addEventListener('trackers-board:comments-last-seen-changed', custom);
  return () => {
    window.removeEventListener('storage', storage);
    window.removeEventListener('trackers-board:comments-last-seen-changed', custom);
  };
}

/**
 * Subscribe to the lastSeen map. Returns the whole map (keyed by
 * `rowSeenKey(trackerId, rowKey)`) plus a setter for one row. The map is
 * re-read on every change so consumers can compare a row's latest-comment
 * timestamp against the stored value.
 */
export function useTrackerCommentsLastSeen(): {
  lastSeen: Store;
  markRowAsSeen: (trackerId: string, rowKey: string, isoTimestamp?: string) => void;
} {
  const lastSeen = useSyncExternalStore(subscribe, readStore, () => ({}));

  const markRowAsSeen = useCallback((trackerId: string, rowKey: string, isoTimestamp?: string) => {
    const stamp = isoTimestamp ?? new Date().toISOString();
    const key = rowSeenKey(trackerId, rowKey);
    const current = readStore();
    if (current[key] === stamp) return;
    writeStore({ ...current, [key]: stamp });
  }, []);

  return { lastSeen, markRowAsSeen };
}

/**
 * One-shot effect that marks a row as seen now. Convenient inside the
 * comment-panel mount lifecycle.
 */
export function useMarkRowSeenOnMount(trackerId: string | null, rowKey: string | null): void {
  useEffect(() => {
    if (!trackerId || !rowKey) return;
    if (typeof window === 'undefined') return;
    const key = rowSeenKey(trackerId, rowKey);
    const current = readStore();
    writeStore({ ...current, [key]: new Date().toISOString() });
  }, [trackerId, rowKey]);
}
